import { useEffect, useState } from 'react'
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from '@/components/ui/collapsible'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useGlossaryDeduplicate } from '@/api/knowledge'
import type {
  DeduplicateGlossaryResponse,
  DedupGroupSummary,
  RejectedTermSummary,
} from '@/types/knowledge'

type Phase = 'loading' | 'preview' | 'executing' | 'complete' | 'error'

interface GlossaryDeduplicationDialogProps {
  open: boolean
  onClose: () => void
}

const PREVIEW_VISIBLE_GROUPS = 100

export function GlossaryDeduplicationDialog({
  open,
  onClose,
}: GlossaryDeduplicationDialogProps) {
  const dedup = useGlossaryDeduplicate()
  const [phase, setPhase] = useState<Phase>('loading')
  const [failedPhase, setFailedPhase] = useState<'dryRun' | 'execute' | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [preview, setPreview] = useState<DeduplicateGlossaryResponse | null>(null)
  const [executeResult, setExecuteResult] = useState<DeduplicateGlossaryResponse | null>(null)

  useEffect(() => {
    if (!open) return
    void runDryRun()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    if (phase !== 'complete') return
    const timer = setTimeout(() => handleClose(), 2000)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase])

  const runDryRun = async () => {
    setPhase('loading')
    setErrorMessage(null)
    setFailedPhase(null)
    try {
      const response = await dedup.mutateAsync({ dryRun: true, skipLlm: false })
      setPreview(response)
      setPhase('preview')
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Dry-run failed')
      setFailedPhase('dryRun')
      setPhase('error')
    }
  }

  const runExecute = async () => {
    setPhase('executing')
    setErrorMessage(null)
    try {
      const response = await dedup.mutateAsync({ dryRun: false, skipLlm: false })
      setExecuteResult(response)
      setPhase('complete')
      toast.success(
        `Deduplication applied — Pass1: ${response.pass1Merged}, Pass2: ${response.pass2Merged}, ` +
        `Pass3: ${response.pass3Synthesized}, Pass4 rejected: ${response.pass4Rejected}`
      )
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Execute failed')
      setFailedPhase('execute')
      setPhase('error')
    }
  }

  const handleClose = () => {
    setPhase('loading')
    setPreview(null)
    setExecuteResult(null)
    setErrorMessage(null)
    setFailedPhase(null)
    onClose()
  }

  const handleRetry = () => {
    if (failedPhase === 'execute') void runExecute()
    else void runDryRun()
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose() }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Glossary Deduplication</DialogTitle>
          <DialogDescription>
            Preview and apply Pass 1–4 deduplication across the glossary.
          </DialogDescription>
        </DialogHeader>

        {phase === 'loading' && (
          <PhaseLoading caption="Analyzing glossary…" />
        )}

        {phase === 'preview' && preview && (
          <PhasePreview
            preview={preview}
            onCancel={handleClose}
            onApply={runExecute}
          />
        )}

        {phase === 'executing' && (
          <PhaseLoading caption="Applying…" />
        )}

        {phase === 'complete' && executeResult && (
          <PhaseComplete result={executeResult} onDone={handleClose} />
        )}

        {phase === 'error' && (
          <PhaseError
            message={errorMessage}
            onRetry={handleRetry}
            onClose={handleClose}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}

function PhaseLoading({ caption }: { caption: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 gap-3">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      <p className="text-sm text-muted-foreground">{caption}</p>
    </div>
  )
}

function PhasePreview({
  preview,
  onCancel,
  onApply,
}: {
  preview: DeduplicateGlossaryResponse
  onCancel: () => void
  onApply: () => void
}) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <StatTile label="Examined" value={preview.termsExamined} />
        <StatTile label="Pass 1 merged" value={preview.pass1Merged} />
        <StatTile label="Pass 2 merged" value={preview.pass2Merged} />
        <StatTile label="Pass 3 synth." value={preview.pass3Synthesized} />
        <StatTile label="Pass 4 rejected" value={preview.pass4Rejected} />
      </div>

      <Section
        title="Merge groups (Pass 1 + Pass 2)"
        empty={preview.pass1Groups.length === 0}
        emptyText="No merges"
      >
        <MergeGroupsList groups={preview.pass1Groups} />
      </Section>

      <Section
        title="Rejected (Pass 4)"
        empty={preview.pass4RejectedItems.length === 0}
        emptyText="No rejections"
      >
        <RejectedTable items={preview.pass4RejectedItems} />
      </Section>

      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={onApply}>Apply Changes</Button>
      </DialogFooter>
    </div>
  )
}

function PhaseComplete({
  result,
  onDone,
}: {
  result: DeduplicateGlossaryResponse
  onDone: () => void
}) {
  return (
    <div className="space-y-4">
      <p className="text-sm">Deduplication applied in {result.durationMs} ms.</p>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <StatTile label="Examined" value={result.termsExamined} />
        <StatTile label="Pass 1 merged" value={result.pass1Merged} />
        <StatTile label="Pass 2 merged" value={result.pass2Merged} />
        <StatTile label="Pass 3 synth." value={result.pass3Synthesized} />
        <StatTile label="Pass 4 rejected" value={result.pass4Rejected} />
      </div>
      <DialogFooter>
        <Button onClick={onDone}>Done</Button>
      </DialogFooter>
    </div>
  )
}

function PhaseError({
  message,
  onRetry,
  onClose,
}: {
  message: string | null
  onRetry: () => void
  onClose: () => void
}) {
  return (
    <div className="space-y-4">
      <Card className="p-4 border-destructive bg-destructive/10">
        <p className="text-sm font-medium">Deduplication failed</p>
        <p className="text-xs text-muted-foreground mt-1">{message ?? 'Unknown error'}</p>
      </Card>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Close</Button>
        <Button onClick={onRetry}>Retry</Button>
      </DialogFooter>
    </div>
  )
}

function StatTile({ label, value }: { label: string; value: number }) {
  return (
    <Card className="p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-2xl font-semibold tabular-nums">{value}</p>
    </Card>
  )
}

function Section({
  title,
  empty,
  emptyText,
  children,
}: {
  title: string
  empty: boolean
  emptyText: string
  children: React.ReactNode
}) {
  return (
    <div className="space-y-2">
      <h3 className="text-sm font-medium">{title}</h3>
      {empty ? (
        <p className="text-xs text-muted-foreground">{emptyText}</p>
      ) : (
        children
      )}
    </div>
  )
}

function MergeGroupsList({ groups }: { groups: DedupGroupSummary[] }) {
  const visible = groups.slice(0, PREVIEW_VISIBLE_GROUPS)
  const hidden = groups.length - visible.length
  return (
    <div className="space-y-1">
      {visible.map((g) => (
        <MergeGroupRow key={g.winnerTermId} group={g} />
      ))}
      {hidden > 0 && (
        <p className="text-xs text-muted-foreground">+{hidden} more</p>
      )}
    </div>
  )
}

function MergeGroupRow({ group }: { group: DedupGroupSummary }) {
  const [open, setOpen] = useState(false)
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-2 w-full text-left px-2 py-1.5 rounded hover:bg-muted/40"
        >
          {open ? (
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
          )}
          <span className="text-sm font-medium truncate">{group.winnerCanonical}</span>
          <span className="text-xs text-muted-foreground ml-auto">
            {group.mergedCanonicals.length} merged · {group.mergedAliasCount} aliases · {group.sourceRefCount} sources
          </span>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <ul className="pl-7 py-1 space-y-0.5">
          {group.mergedCanonicals.map((c) => (
            <li key={c} className="text-xs text-muted-foreground">{c}</li>
          ))}
        </ul>
      </CollapsibleContent>
    </Collapsible>
  )
}

function RejectedTable({ items }: { items: RejectedTermSummary[] }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Term</TableHead>
          <TableHead>Matched rule</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((item) => (
          <TableRow key={item.termId}>
            <TableCell className="text-sm">{item.canonicalName}</TableCell>
            <TableCell className="text-xs font-mono text-muted-foreground">{item.matchedRule}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
